import { useState, useEffect } from "react";
import { ProjectSelectionService } from "shared/services/project";
import {
  RevisionProjectHistoryService,
  DownloadRevisionFilesService,
} from "shared/services/revision";
import {
  handleErrors,
  formatFileSize,
  downloadBlobAsFile,
} from "shared/utils/helper";
import { filenameFromContentDisposition } from "shared/utils/contentDisposition";
import { Icons, Images } from "assets";
import classNames from "classnames";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomDocuments from "shared/components/dashboardCommon/customDocuments";
import CustomSelect from "shared/components/common/customSelect";
import Spinner from "shared/components/common/spinner";
import NoContentCard from "shared/components/common/noContentCard";
import DocumentCardSkeleton from "shared/components/common/documentCardSkeleton";

interface DocumentsPageProps {
  title?: string;
  description?: string;
  styles: any;
}

const DocumentsPage = ({
  title = "All Documents",
  description = "Access all your project documents in one place.",
  styles,
}: DocumentsPageProps) => {
  const [workingProjects, setWorkingProjects] = useState<any[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null,
  );
  const [revisions, setRevisions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingRevisions, setIsLoadingRevisions] = useState<boolean>(false);
  const [downloadingRevisionId, setDownloadingRevisionId] = useState<
    number | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [revisionsError, setRevisionsError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await ProjectSelectionService(false);
        const projects = response?.data?.data || [];

        const mappedProjects = projects.map((project: any) => ({
          project_id: project.project_id,
          title: project.name,
          image: Images.WP1,
          currentRevision: "Rev A",
          nextRevisionDate: "-",
          status: "active",
          isHaveNotification: false,
          canSubmitRevision: false,
        }));

        setWorkingProjects(mappedProjects);

        if (mappedProjects.length > 0) {
          setSelectedProject(mappedProjects[0]?.title || "");
          setSelectedProjectId(mappedProjects[0]?.project_id || null);
        }
      } catch (err: any) {
        handleErrors(err);
        setError(err?.response?.data?.message || "Failed to load projects");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchRevisions = async () => {
      if (!selectedProjectId) return;

      try {
        setIsLoadingRevisions(true);
        setRevisionsError(null);
        const response = await RevisionProjectHistoryService(selectedProjectId);
        const revisionsData = response?.data?.data || [];

        const mappedRevisions = revisionsData.map((revision: any) => ({
          revision_id: revision.revision_id,
          project_id: revision.project_id,
          title: revision.revision_name,
          currentRevision: revision.project_name,
          nextRevisionDate: new Date(
            revision.revision_created_at,
          ).toLocaleDateString("en-US", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          status: revision.revision_status,
          size: formatFileSize(revision.total_size),
          revision_created_at: revision.revision_created_at,
          total_size: revision.total_size,
          isHaveNotification: false,
          canSubmitRevision: revision.revision_status === "in_review",
        }));

        setRevisions(mappedRevisions);
      } catch (err: any) {
        handleErrors(err);
        setRevisionsError(
          err?.response?.data?.message || "Failed to load revisions",
        );
      } finally {
        setIsLoadingRevisions(false);
      }
    };

    fetchRevisions();
  }, [selectedProjectId]);

  const handleProjectChange = (item: any) => {
    setSelectedProject(item.title);
    setSelectedProjectId(item.project_id);
  };

  const projectOptions = workingProjects.map((project, index) => ({
    ...project,
    id: project.project_id || index,
    label: project.title,
    value: project.title,
  }));

  const handleDownload = async (revisionId: number, revisionName: string) => {
    try {
      setDownloadingRevisionId(revisionId);
      const response = await DownloadRevisionFilesService(revisionId);
      downloadBlobAsFile(
        response.data,
        filenameFromContentDisposition(
          response.headers?.["content-disposition"],
        ) || `${revisionName}.zip`,
      );
    } catch (err: any) {
      handleErrors(err);
    } finally {
      setDownloadingRevisionId(null);
    }
  };

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col gap-6 w-full h-full",
      )}
    >
      <div className="sm:pr-0 pr-12">
        <CustomPageHeading
          title={title}
          description={description}
          size="large"
        />
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center p-10 text-gray-500">
          <Spinner />
        </div>
      ) : error ? (
        <div className="flex w-full items-center justify-center p-10 text-red-500">
          {error}
        </div>
      ) : workingProjects.length === 0 ? (
        <div
          className={classNames(
            styles.noContentCardContainer,
            "flex items-center justify-center h-[700px] w-full",
          )}
        >
          <NoContentCard
            Icon={Icons.NoHome}
            customIconContianer={classNames(styles.noContentCardIcon)}
            label1="No projects available."
          />
        </div>
      ) : (
        <>
          <div className={classNames(styles.box, "cursor-pointer")}>
            <CustomSelect
              label=""
              required
              options={projectOptions}
              customSortStyle={styles.projectSelectContainer}
              customSortLabelStyle={styles.border}
              customDropdownMenu={styles.customDropdownMenu}
              defaultActiveItem={selectedProject}
              onChange={handleProjectChange}
              enableIconTrigger={true}
            />
          </div>

          <div className={classNames(styles.listedDocuments)}>
            {isLoadingRevisions ? (
              <DocumentCardSkeleton count={3} showBorder={true} />
            ) : revisionsError ? (
              <div className="flex w-full items-center justify-center p-10 text-red-500">
                {revisionsError}
              </div>
            ) : revisions.length > 0 ? (
              revisions.map((revision, index) => (
                <CustomDocuments
                  key={revision.revision_id || index}
                  data={revision}
                  size={revision.size}
                  date={revision.nextRevisionDate}
                  showBorder={true}
                  onDownload={() =>
                    handleDownload(revision.revision_id, revision.title)
                  }
                  isDownloading={downloadingRevisionId === revision.revision_id}
                />
              ))
            ) : (
              <div
                className={classNames(
                  styles.noContentCardContainer,
                  "flex items-center justify-center h-[700px] w-full",
                )}
              >
                <NoContentCard
                  Icon={Icons.NoRevisions}
                  customIconContianer={classNames(styles.noContentCardIcon)}
                  label1="No media yet"
                />
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentsPage;
